import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Trash2, Plus, Maximize } from 'lucide-react';
import StageOverlay, { HelpIcon } from '../Overlays/StageOverlay';

const TILE_W = 200;
const TILE_H = 50;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2;
const FIT_PADDING = 60;

// Canvas layout — no framework boxes. Two halves divided by a vertical midline.
// Coordinates are in canvas space (pre-transform).
const CANVAS_W = 1200;
const CANVAS_H = 700;
const MID_X = CANVAS_W / 2;          // vertical midline; left = pillar 1, right = pillar 2

// Positions are clamped AT WRITE TIME so a tile can never be stored outside
// the canvas. Display code must render stored positions as-is: Build, the
// final map (NestedEggCanvas), and the share view all have to show the same
// thing, so never clamp at display time.
const EDGE_PAD = 16; // breathing room so a clamped tile never touches an edge
export function clampToCanvas(x, y) {
  return {
    x: Math.max(EDGE_PAD, Math.min(CANVAS_W - TILE_W - EDGE_PAD, x)),
    y: Math.max(EDGE_PAD, Math.min(CANVAS_H - TILE_H - EDGE_PAD, y)),
  };
}

// Deterministic pseudo-random based on tile id, so the same tile always lands in the same spot
function seededFromId(id, salt = 0) {
  let h = salt;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return (h % 10000) / 10000;
}

// Looping demo shown inside the Align stage modal: a black tile slides from
// the left half (turning teal) to the right half (turning gold) and back.
function ColorSwapDemo() {
  return (
    <div className="mt-4 rounded-xl border border-black/10 overflow-hidden bg-white">
      <div className="relative h-24 flex">
        <div className="flex-1 bg-teal/40" />
        <div className="flex-1 bg-gold/40" />
        <div className="absolute top-0 left-1/2 w-px h-full bg-navy/30" />
        <div className="absolute top-1/2 -translate-y-1/2 color-swap-tile">
          <span className="text-sm font-bold uppercase tracking-wide">Concept</span>
        </div>
      </div>
      <p className="text-[11px] text-center text-txt-secondary py-2 px-3 italic">
        Drag a concept to either side and it changes color to match.
      </p>
    </div>
  );
}

// Looping demo for Prioritize overlay: 3 pills reorder themselves top-to-bottom.
function PrioritizeDemo() {
  return (
    <div className="mt-3 rounded-xl border border-black/10 overflow-hidden bg-white">
      <div className="flex flex-col items-center px-6 py-3">
        <div className="text-[9px] font-heading font-bold uppercase tracking-widest text-txt-secondary/60 mb-1">
          Highest impact
        </div>
        <div style={{ position: 'relative', width: 160, height: 106 }}>
          <div className="prioritize-pill-1" style={{ position: 'absolute', left: 0, width: 150 }}>
            <div className="bg-teal/70 border border-teal-dark rounded-lg px-3 py-1.5 text-xs font-bold text-black text-center">Concept A</div>
          </div>
          <div className="prioritize-pill-2" style={{ position: 'absolute', left: 0, width: 150 }}>
            <div className="bg-teal/50 border border-teal-dark/70 rounded-lg px-3 py-1.5 text-xs font-bold text-black text-center">Concept B</div>
          </div>
          <div className="prioritize-pill-3" style={{ position: 'absolute', left: 0, width: 150 }}>
            <div className="bg-teal/30 border border-teal-dark/50 rounded-lg px-3 py-1.5 text-xs font-bold text-black/80 text-center">Concept C</div>
          </div>
        </div>
        <div className="text-[9px] font-heading font-bold uppercase tracking-widest text-txt-secondary/60 mt-1">
          Lowest impact
        </div>
      </div>
      <p className="text-[11px] text-center text-txt-secondary pb-2.5 px-3 italic">
        Drag concepts up or down to set the order of impact.
      </p>
    </div>
  );
}

// Compute a visual position for a tile. If the tile has a saved position, use it.
// Otherwise, return a deterministic seeded scatter based on its zone + id.
// IMPORTANT: this is render-only — never written back to the DB unless the user drags the tile.
function getDisplayPos(tile) {
  if (tile.posX || tile.posY) return { x: tile.posX || 0, y: tile.posY || 0 };
  const rx = seededFromId(tile.id, 1);
  const ry = seededFromId(tile.id, 7);
  if (tile.zone === 'PROOF_1' || tile.zone === 'SCAFFOLDING_1') {
    return { x: 80 + rx * 420, y: 80 + ry * 540 };          // left half
  }
  if (tile.zone === 'PROOF_2' || tile.zone === 'SCAFFOLDING_2') {
    return { x: MID_X + 80 + rx * 420, y: 80 + ry * 540 };  // right half
  }
  // HOLDING_PEN — full canvas
  return { x: 80 + rx * 1000, y: 80 + ry * 540 };
}

// Stages
const STAGE_BIG_IDEA = 1;
const STAGE_MUST_UNDERSTAND = 2;
const STAGE_ALIGN = 3;
const STAGE_PRIORITIZE = 4;

export default function OrganizeMode({
  tiles,
  mapName,
  onUpdateTile,
  onCutTile,
  onRestoreTile,
  onDeleteTile,
  onAddTile,
  onScaleChange,
  onComplete,
}) {
  const viewportRef = useRef(null);
  const wrapperRef = useRef(null);
  const [scale, setScaleInternal] = useState(0.85);
  const onScaleChangeRef = useRef(onScaleChange);
  onScaleChangeRef.current = onScaleChange;
  const setScale = useCallback((s) => { setScaleInternal(s); if (onScaleChangeRef.current) onScaleChangeRef.current(s); }, []);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [panning, setPanning] = useState(null);
  const [spaceHeld, setSpaceHeld] = useState(false);

  // Track spacebar for intentional panning (spacebar + drag)
  useEffect(() => {
    function down(e) {
      if (e.code === 'Space' && !e.repeat) {
        // Don't intercept spacebar when user is typing in an input or textarea
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
        e.preventDefault();
        setSpaceHeld(true);
      }
    }
    function up(e) { if (e.code === 'Space') setSpaceHeld(false); }
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);
  const [hoverZone, setHoverZone] = useState(null);
  const hoverZoneEnteredAt = useRef(null);
  const [dragScreenPos, setDragScreenPos] = useState(null);
  const lastCanvasCursor = useRef(null);
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const addInputRef = useRef(null);
  const didAutoFit = useRef(false);
  const [editingZoneTile, setEditingZoneTile] = useState(null);
  const [editingZoneText, setEditingZoneText] = useState('');
  const zoneTileEditRef = useRef(null);

  // Stage state
  const [stage, setStage] = useState(STAGE_BIG_IDEA);
  const [showOverlay, setShowOverlay] = useState(true);
  const [fadeZones, setFadeZones] = useState([]);

  // Tile subsets
  const holdingTiles = tiles.filter((t) => t.zone === 'HOLDING_PEN');
  const bigIdeaTile = tiles.find((t) => t.zone === 'BIG_IDEA') || null;
  const scaffold1Tile = tiles.find((t) => t.zone === 'SCAFFOLDING_1') || null;
  const scaffold2Tile = tiles.find((t) => t.zone === 'SCAFFOLDING_2') || null;
  const proof1Tiles = tiles.filter((t) => t.zone === 'PROOF_1').sort((a, b) => a.sortOrder - b.sortOrder);
  const proof2Tiles = tiles.filter((t) => t.zone === 'PROOF_2').sort((a, b) => a.sortOrder - b.sortOrder);
  const cutTiles = tiles.filter((t) => t.zone === 'CUT');

  // Auto-detect stage from existing data
  useEffect(() => {
    if (proof1Tiles.length > 0 || proof2Tiles.length > 0) {
      setStage(STAGE_PRIORITIZE);
      setShowOverlay(false);
    } else if (scaffold1Tile && scaffold2Tile) {
      setStage(STAGE_ALIGN);
      setShowOverlay(false);
    } else if (bigIdeaTile) {
      setStage(STAGE_MUST_UNDERSTAND);
      setShowOverlay(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No DB migration. Tiles with no saved position get a deterministic visual scatter
  // (computed via getDisplayPos below) — only persisted when the user actually drags them.

  useEffect(() => {
    if (adding && addInputRef.current) addInputRef.current.focus();
  }, [adding]);

  useEffect(() => {
    if (editingZoneTile && zoneTileEditRef.current) {
      zoneTileEditRef.current.focus();
      zoneTileEditRef.current.select();
    }
  }, [editingZoneTile]);

  function saveZoneTileEdit() {
    if (!editingZoneTile) return;
    const trimmed = editingZoneText.trim();
    if (trimmed && trimmed !== tiles.find(t => t.id === editingZoneTile)?.text) {
      onUpdateTile(editingZoneTile, { text: trimmed });
    }
    setEditingZoneTile(null);
  }

  // Auto-fit disabled on initial load — was zooming out too far and clustering tiles.
  // Users can click Fit All to fit explicitly. Initial scale is set in the layout effect below.

  function fitAll() {
    if (!viewportRef.current) return;
    const vp = viewportRef.current.getBoundingClientRect();
    // Fit the full canvas (CANVAS_W x CANVAS_H) plus any out-of-bounds tiles.
    let minX = 0, minY = 0;
    let maxX = CANVAS_W, maxY = CANVAS_H;
    tiles.forEach((t) => {
      if (t.zone === 'CUT' || t.zone === 'BIG_IDEA') return;
      const tx = t.posX || 0;
      const ty = t.posY || 0;
      if (tx < minX) minX = tx;
      if (ty < minY) minY = ty;
      if (tx + TILE_W > maxX) maxX = tx + TILE_W;
      if (ty + TILE_H > maxY) maxY = ty + TILE_H;
    });
    const contentW = maxX - minX + FIT_PADDING * 2;
    const contentH = maxY - minY + FIT_PADDING * 2;
    const scaleX = vp.width / contentW;
    const scaleY = vp.height / contentH;
    const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(scaleX, scaleY)));
    setScale(newScale);
    setTranslate({
      x: 0, // CSS mx-auto handles horizontal centering
      y: 10 - minY * newScale,
    });
  }

  useEffect(() => {
    if (!viewportRef.current) return;
    // ResizeObserver waits for non-zero dimensions (handles display:none mount),
    // fits ONCE, then disconnects. Window resizes do NOT recompute — user clicks
    // Fit All if they need to re-fit after resizing.
    const el = viewportRef.current;
    const ro = new ResizeObserver((entries) => {
      if (didAutoFit.current) return;
      const r = entries[0].contentRect;
      if (r.width === 0 || r.height === 0) return;
      didAutoFit.current = true;
      const sW = r.width / (CANVAS_W + 40);
      const sH = r.height / (CANVAS_H + 40);
      const s = Math.max(0.55, Math.min(1.0, sW, sH));
      setScale(s);
      setTranslate({ x: 0, y: 10 }); // x=0: CSS mx-auto handles horizontal centering
      ro.disconnect();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [setScale]);

  // --- Zone hit-testing ---
  const zoneRefs = useRef({});
  const registerZone = useCallback((zone, el) => {
    zoneRefs.current[zone] = el;
  }, []);

  function screenToCanvas(clientX, clientY) {
    if (!viewportRef.current) return { x: 0, y: 0 };
    const vp = viewportRef.current.getBoundingClientRect();
    // CSS mx-auto centers the canvas. transformOrigin is center-top.
    // The visual left edge of the scaled canvas = viewport center - (CANVAS_W * scale / 2) + translate.x * scale
    const canvasVisualLeft = vp.left + vp.width / 2 - (CANVAS_W * scale) / 2 + translate.x * scale;
    const canvasVisualTop = vp.top + translate.y * scale;
    return {
      x: (clientX - canvasVisualLeft) / scale,
      y: (clientY - canvasVisualTop) / scale,
    };
  }

  function getVisibleRandomPos() {
    if (!viewportRef.current) {
      return {
        x: CANVAS_W * 0.3 + Math.random() * (CANVAS_W * 0.4),
        y: CANVAS_H * 0.4 + Math.random() * (CANVAS_H * 0.3),
      };
    }
    // Spawn in the CENTER 50% of the visible viewport so users always see new tiles
    const vp = viewportRef.current.getBoundingClientRect();
    const topLeft = screenToCanvas(vp.left + vp.width * 0.25, vp.top + vp.height * 0.3);
    const bottomRight = screenToCanvas(vp.right - vp.width * 0.25, vp.bottom - vp.height * 0.2);
    const w = Math.max(200, bottomRight.x - topLeft.x);
    const h = Math.max(100, bottomRight.y - topLeft.y);
    return clampToCanvas(topLeft.x + Math.random() * w, topLeft.y + Math.random() * h);
  }

  // --- Zoom ---
  function handleWheel(e) {
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale * zoomFactor));
    setTranslate({
      x: 0, // CSS mx-auto handles horizontal centering
      y: 10,
    });
    setScale(newScale);
  }

  function handleViewportPointerDown(e) {
    if (e.target !== e.currentTarget && !e.target.closest('[data-canvas-bg]')) return;
    if (e.button !== 0) return;
    // Only pan if spacebar is held — prevents accidental canvas drag
    if (!spaceHeld) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setPanning({ startX: e.clientX, startY: e.clientY, startTx: translate.x, startTy: translate.y });
  }

  const pendingDrag = useRef(null);

  function handleTilePointerDown(e, tile) {
    if (e.button !== 0) return;
    e.stopPropagation();
    // Don't preventDefault or start drag yet — wait for movement so dblclick can fire
    pendingDrag.current = {
      tile,
      startClientX: e.clientX,
      startClientY: e.clientY,
      pointerId: e.pointerId,
    };
  }

  function commitDrag(clientX, clientY) {
    const pd = pendingDrag.current;
    if (!pd) return;
    pendingDrag.current = null;
    if (wrapperRef.current) wrapperRef.current.setPointerCapture(pd.pointerId);
    const { x, y } = screenToCanvas(pd.startClientX, pd.startClientY);
    // Use getDisplayPos so 0,0 tiles use their visual scatter position for the drag offset
    const dp = getDisplayPos(pd.tile);
    setDragging({
      tileId: pd.tile.id,
      offsetX: x - dp.x,
      offsetY: y - dp.y,
      fromZone: pd.tile.zone,
      pointerId: pd.pointerId,
      startClientX: pd.startClientX,
      startClientY: pd.startClientY,
    });
    setDragPos({ x: dp.x, y: dp.y });
    if (wrapperRef.current) {
      const wr = wrapperRef.current.getBoundingClientRect();
      setDragScreenPos({ x: clientX - wr.left - 90, y: clientY - wr.top - 20 });
    }
  }

  function handlePointerMove(e) {
    // Check pending drag — commit after movement threshold
    if (pendingDrag.current && !dragging) {
      const dx = e.clientX - pendingDrag.current.startClientX;
      const dy = e.clientY - pendingDrag.current.startClientY;
      if (Math.abs(dx) + Math.abs(dy) >= 4) {
        commitDrag(e.clientX, e.clientY);
      }
      return;
    }
    if (panning) {
      e.preventDefault();
      setTranslate({
        x: panning.startTx + (e.clientX - panning.startX),
        y: panning.startTy + (e.clientY - panning.startY),
      });
      return;
    }
    if (dragging) {
      e.preventDefault();
      const { x, y } = screenToCanvas(e.clientX, e.clientY);
      setDragPos({ x: x - dragging.offsetX, y: y - dragging.offsetY });
      lastCanvasCursor.current = { x, y };
      if (wrapperRef.current) {
        const wr = wrapperRef.current.getBoundingClientRect();
        setDragScreenPos({ x: e.clientX - wr.left - 90, y: e.clientY - wr.top - 20 });
      }

      // Compute hover target — check screen-space header refs first, fall back to canvas side.
      function checkScreenZone(zoneKey) {
        const el = zoneRefs.current[zoneKey];
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      }
      let zone = null;
      if (checkScreenZone('BIG_IDEA')) zone = 'BIG_IDEA';
      else if (checkScreenZone('SCAFFOLDING_1')) zone = 'SCAFFOLDING_1';
      else if (checkScreenZone('SCAFFOLDING_2')) zone = 'SCAFFOLDING_2';
      else {
        // Otherwise: which canvas side is the cursor over?
        const dropX = x - dragging.offsetX + TILE_W / 2;
        zone = dropX < MID_X ? 'LEFT' : 'RIGHT';
      }
      if (zone !== hoverZone) {
        hoverZoneEnteredAt.current = zone ? Date.now() : null;
        setHoverZone(zone);
      }
    }
  }

  function handlePointerUp(e) {
    // No drag movement crossed threshold → ignore (was a click, not a drag)
    if (pendingDrag.current && !dragging) {
      pendingDrag.current = null;
      return;
    }
    pendingDrag.current = null;
    if (wrapperRef.current) {
      try { wrapperRef.current.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    if (panning) { setPanning(null); return; }
    if (!dragging) return;

    const tile = tiles.find((t) => t.id === dragging.tileId);
    if (!tile) {
      setDragging(null); setDragPos(null); setDragScreenPos(null);
      setHoverZone(null); hoverZoneEnteredAt.current = null; lastCanvasCursor.current = null;
      return;
    }

    // 1) Hit-test the screen-space header drop targets (Big Idea + 2 pillar pills)
    const allowedZones = getActiveZones();
    function hitTest(zoneKey) {
      const el = zoneRefs.current[zoneKey];
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    }

    let headerHit = null;
    if (hitTest('BIG_IDEA')) headerHit = 'BIG_IDEA';
    else if (hitTest('SCAFFOLDING_1')) headerHit = 'SCAFFOLDING_1';
    else if (hitTest('SCAFFOLDING_2')) headerHit = 'SCAFFOLDING_2';

    if (headerHit && allowedZones.includes(headerHit)) {
      // Bump existing tile out of this slot first
      const existing = tiles.find((t) => t.zone === headerHit && t.id !== tile.id);
      if (existing) {
        const pos = getVisibleRandomPos();
        onUpdateTile(existing.id, { zone: 'HOLDING_PEN', posX: pos.x, posY: pos.y });
      }
      onUpdateTile(tile.id, { zone: headerHit, posX: 0, posY: 0, sortOrder: 0 });
      if (showOverlay) dismissOverlay();

      // Auto-advance from Big Idea stage after a short delay
      if (headerHit === 'BIG_IDEA' && stage === STAGE_BIG_IDEA) {
        setTimeout(() => { setStage(STAGE_MUST_UNDERSTAND); setShowOverlay(true); }, 1500);
      }
    } else if (lastCanvasCursor.current) {
      // 2) Drop landed in the canvas. Clamp into bounds first so the stored
      // position is visible in every view, then decide pillar from posX vs midline.
      const { x: dropX, y: dropY } = clampToCanvas(
        lastCanvasCursor.current.x - dragging.offsetX,
        lastCanvasCursor.current.y - dragging.offsetY
      );
      const isLeftSide = dropX + TILE_W / 2 < MID_X;

      // LEFT side = PROOF_1 (teal), RIGHT side = PROOF_2 (gold)
      let targetZone = null;
      if (isLeftSide && allowedZones.includes('PROOF_1')) targetZone = 'PROOF_1';
      else if (!isLeftSide && allowedZones.includes('PROOF_2')) targetZone = 'PROOF_2';

      if (targetZone) {
        onUpdateTile(tile.id, { zone: targetZone, posX: dropX, posY: dropY, sortOrder: 0 });
        if (showOverlay) dismissOverlay();
      } else {
        // No allowed target — return to holding pen at the drop position
        if (dragging.fromZone === 'HOLDING_PEN') {
          onUpdateTile(tile.id, { posX: dropX, posY: dropY });
        } else {
          onUpdateTile(tile.id, { zone: 'HOLDING_PEN', posX: dropX, posY: dropY });
        }
      }
    }

    setDragging(null);
    setDragPos(null);
    setDragScreenPos(null);
    setHoverZone(null);
    hoverZoneEnteredAt.current = null;
    lastCanvasCursor.current = null;
  }

  function handleAddTile() {
    const trimmed = newText.trim();
    if (trimmed) {
      const pos = getVisibleRandomPos();
      onAddTile(trimmed, pos.x, pos.y);
      setNewText('');
    }
  }


  function zoomBy(factor) {
    if (!viewportRef.current) return;
    const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale * factor));
    setTranslate({
      x: 0, // CSS mx-auto handles horizontal centering
      y: 10,
    });
    setScale(newScale);
  }

  // --- Stage logic ---
  function getActiveZones() {
    switch (stage) {
      case STAGE_BIG_IDEA: return ['BIG_IDEA'];
      case STAGE_MUST_UNDERSTAND: return ['BIG_IDEA', 'SCAFFOLDING_1', 'SCAFFOLDING_2'];
      case STAGE_ALIGN: return ['BIG_IDEA', 'SCAFFOLDING_1', 'SCAFFOLDING_2', 'PROOF_1', 'PROOF_2'];
      case STAGE_PRIORITIZE: return ['BIG_IDEA', 'SCAFFOLDING_1', 'SCAFFOLDING_2', 'PROOF_1', 'PROOF_2'];
      default: return [];
    }
  }

  function advanceStage() {
    const next = stage + 1;
    setStage(next);
    setShowOverlay(true);
  }

  function canAdvance() {
    switch (stage) {
      case STAGE_BIG_IDEA: return !!bigIdeaTile;
      case STAGE_MUST_UNDERSTAND: return !!scaffold1Tile && !!scaffold2Tile;
      case STAGE_ALIGN: return holdingTiles.length === 0;
      case STAGE_PRIORITIZE: return true;
      default: return false;
    }
  }

  function getAdvanceText() {
    switch (stage) {
      case STAGE_BIG_IDEA: return null; // auto-advance, no button
      case STAGE_MUST_UNDERSTAND: return "Both Are Set";
      case STAGE_ALIGN: return "Ready To Get Clearer?";
      case STAGE_PRIORITIZE: return "Click When Its Clear";
      default: return "";
    }
  }

  // Shown in the advance button's spot while the stage gate is unsatisfied,
  // so the user can see what the app is waiting for instead of no button.
  function getBlockedText() {
    switch (stage) {
      case STAGE_MUST_UNDERSTAND: return "Set Both Scaffolds First";
      case STAGE_ALIGN:
        return holdingTiles.length === 1
          ? "1 Concept Still Unplaced"
          : `${holdingTiles.length} Concepts Still Unplaced`;
      default: return null;
    }
  }

  // Show pillar background washes?
  const showScaffoldZones = stage >= STAGE_MUST_UNDERSTAND;

  const zoomPercent = Math.round(scale * 100);

  // --- Zones to highlight ---
  function getStageZones() {
    switch (stage) {
      case STAGE_BIG_IDEA: return ['BIG_IDEA'];
      case STAGE_MUST_UNDERSTAND: return ['SCAFFOLDING_1', 'SCAFFOLDING_2'];
      case STAGE_ALIGN: return ['PROOF_1', 'PROOF_2'];
      case STAGE_PRIORITIZE: return ['PROOF_1', 'PROOF_2'];
      default: return [];
    }
  }
  const highlightZones = showOverlay ? getStageZones() : [];

  function dismissOverlay() {
    const zones = getStageZones();
    setShowOverlay(false);
    setFadeZones(zones);
    setTimeout(() => setFadeZones([]), 3000);
  }

  function zoneHighlightClass(zone) {
    if (highlightZones.includes(zone)) return 'zone-highlight';
    if (fadeZones.includes(zone)) return 'zone-highlight-fade';
    return '';
  }

  // --- Overlay content per stage ---
  function renderOverlay() {
    if (!showOverlay) return null;

    // Dynamic names for overlay copy
    const s1Name = scaffold1Tile?.text || 'Scaffold 1';
    const s2Name = scaffold2Tile?.text || 'Scaffold 2';

    const overlays = {
      [STAGE_BIG_IDEA]: (
        <StageOverlay onDismiss={dismissOverlay}>
          <p>You named this <strong>{mapName || 'your map'}</strong>.</p>
          <p>When you are done communicating this, what is the one thing your audience must walk away understanding?</p>
          <p>Look at what is on this canvas. Find it. Drag it in. If it is not there, add it.</p>
        </StageOverlay>
      ),
      [STAGE_MUST_UNDERSTAND]: (
        <StageOverlay onDismiss={dismissOverlay}>
          <p>What are the two things they must fully grasp before your Big Idea lands with your audience?</p>
          <p>Drag them in. If they are not there, add them.</p>
        </StageOverlay>
      ),
      [STAGE_ALIGN]: (
        <StageOverlay onDismiss={dismissOverlay}>
          <p>Every concept on this map supports one of your two scaffolds. Drag each concept under the one it supports.</p>
          <div className="my-3 flex gap-3">
            <div className="flex-1 bg-teal/20 border-2 border-teal-dark rounded-xl px-4 py-2.5 text-center">
              <p className="text-[9px] font-heading font-bold uppercase tracking-widest text-teal-dark mb-0.5">Scaffold 1</p>
              <p className="text-base font-bold">{s1Name}</p>
            </div>
            <div className="flex-1 bg-gold/20 border-2 border-gold-dark rounded-xl px-4 py-2.5 text-center">
              <p className="text-[9px] font-heading font-bold uppercase tracking-widest text-gold-dark mb-0.5">Scaffold 2</p>
              <p className="text-base font-bold">{s2Name}</p>
            </div>
          </div>
          <p>If it does not support either one, it does not belong. Remove it. If you see something missing you can always + ADD CONCEPT above.</p>
          <ColorSwapDemo />
          <p className="text-sm text-txt-secondary italic mt-1">Once support is chosen it changes color to match.</p>
        </StageOverlay>
      ),
      [STAGE_PRIORITIZE]: (
        <StageOverlay onDismiss={dismissOverlay}>
          <p>Order each scaffold's concepts by impact. Most impactful on top, least on the bottom.</p>
          <div className="my-3 space-y-2">
            <div className="bg-teal/20 border-2 border-teal-dark rounded-xl px-4 py-2.5">
              <p className="text-[9px] font-heading font-bold uppercase tracking-widest text-teal-dark mb-0.5">Start with</p>
              <p className="text-base font-bold">{s1Name}</p>
            </div>
            <div className="bg-gold/20 border-2 border-gold-dark rounded-xl px-4 py-2.5">
              <p className="text-[9px] font-heading font-bold uppercase tracking-widest text-gold-dark mb-0.5">Then do the same for</p>
              <p className="text-base font-bold">{s2Name}</p>
            </div>
          </div>
          <PrioritizeDemo />
        </StageOverlay>
      ),
    };

    return overlays[stage] || null;
  }

  // --- Help text per stage ---
  function renderHelp() {
    const helpText = {
      [STAGE_BIG_IDEA]: "Find the one thing your audience must walk away understanding. Drag it into The Big Idea zone.",
      [STAGE_MUST_UNDERSTAND]: "Find the two things they must grasp before your Big Idea lands. Drag them into the zones below.",
      [STAGE_ALIGN]: "Each remaining concept feeds one of the two Must Understands. Drag it there, or remove it.",
      [STAGE_PRIORITIZE]: "Order concepts by impact within each zone. Top = most critical. Drag to reorder.",
    };
    return <HelpIcon>{helpText[stage]}</HelpIcon>;
  }

  return (
    <div
      ref={wrapperRef}
      className="flex-1 flex flex-col overflow-hidden"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {/* Stage overlay */}
      {renderOverlay()}

      {/* === HEADER ROW: stage label LEFT, Big Idea pill ABSOLUTE CENTER, actions RIGHT === */}
      <div className="flex-shrink-0 relative bg-white border-b-2 border-black shadow-sm" style={{ minHeight: 62 }}>
        {/* LEFT: clickable step toggle — jump between any of the 4 build sub-stages */}
        <div className="absolute left-5 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {renderHelp()}
          <div className="flex items-center gap-1">
            {[
              { stage: STAGE_BIG_IDEA, label: '2 · Big Idea' },
              { stage: STAGE_MUST_UNDERSTAND, label: '3 · Scaffolds' },
              { stage: STAGE_ALIGN, label: '4 · Align' },
              { stage: STAGE_PRIORITIZE, label: '5 · Prioritize' },
            ].map((s) => (
              <button
                key={s.stage}
                onClick={() => { setStage(s.stage); setShowOverlay(true); }}
                className={`text-xs font-heading font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md transition-all whitespace-nowrap ${
                  stage === s.stage
                    ? 'bg-gold text-black border-2 border-gold-dark shadow-md'
                    : 'bg-white text-txt-secondary border-2 border-black/15 hover:border-gold-dark/50 hover:text-black'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* ABSOLUTE CENTER: Big Idea pill, alone — mt-3 gives room for the notched label */}
        <div className="flex items-center justify-center mt-3" style={{ minHeight: 62 }}>
          <div
            ref={(el) => registerZone('BIG_IDEA', el)}
            className={`relative rounded-2xl transition-all flex items-center justify-center px-7 py-2 ${
              hoverZone === 'BIG_IDEA'
                ? 'ring-4 ring-gold-dark scale-[1.02] shadow-2xl'
                : 'shadow-lg'
            } ${zoneHighlightClass('BIG_IDEA')}`}
            style={{
              minHeight: 50,
              minWidth: 240,
              maxWidth: 480,
              background: 'linear-gradient(90deg, #E87722 0%, #F09A50 50%, #E87722 100%)',
              border: '3px solid #C45E10',
            }}
          >
            <div className="absolute -top-2.5 left-5 px-2 text-[10px] font-heading font-bold uppercase tracking-widest text-gold-dark bg-white leading-none py-0.5">
              The Big Idea
            </div>
            {bigIdeaTile ? (
              editingZoneTile === bigIdeaTile.id ? (
                <input ref={zoneTileEditRef} type="text" value={editingZoneText} maxLength={60}
                  onChange={(e) => setEditingZoneText(e.target.value)}
                  onBlur={saveZoneTileEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); saveZoneTileEdit(); }
                    if (e.key === 'Escape') setEditingZoneTile(null);
                  }}
                  className="bg-transparent text-black text-center font-heading font-bold text-base uppercase tracking-wide border-none outline-none focus:ring-0 w-full" />
              ) : (
                <div
                  className={`text-black font-heading font-bold uppercase tracking-wide cursor-grab active:cursor-grabbing text-center line-clamp-2 ${
                    bigIdeaTile.text.length > 40 ? 'text-sm' : bigIdeaTile.text.length > 25 ? 'text-base' : 'text-xl'
                  }`}
                  onPointerDown={(e) => handleTilePointerDown(e, bigIdeaTile)}
                  onDoubleClick={() => { setEditingZoneTile(bigIdeaTile.id); setEditingZoneText(bigIdeaTile.text); }}
                  title={bigIdeaTile.text}
                >
                  {bigIdeaTile.text}
                </div>
              )
            ) : (
              <span className="text-sm text-black/60 italic font-body font-semibold whitespace-nowrap">
                Drag your Big Idea here
              </span>
            )}
          </div>
        </div>

        {/* RIGHT: advance button + Add Concept */}
        <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {getAdvanceText() && stage <= STAGE_PRIORITIZE && (canAdvance() ? (
            <button
              onClick={stage === STAGE_PRIORITIZE && onComplete ? onComplete : advanceStage}
              className="bg-navy text-white h-11 px-5 rounded-lg flex items-center text-base font-heading font-bold uppercase tracking-wide shadow-md hover:bg-navy-light transition-colors whitespace-nowrap"
            >
              {getAdvanceText()}
            </button>
          ) : getBlockedText() ? (
            <button
              disabled
              title="Drag every remaining concept under a scaffold, or remove it, to continue"
              className="bg-white text-txt-secondary border-2 border-black/15 h-11 px-5 rounded-lg flex items-center text-base font-heading font-bold uppercase tracking-wide whitespace-nowrap cursor-default"
            >
              {getBlockedText()}
            </button>
          ) : null)}
          {adding ? (
            <input
              ref={addInputRef}
              type="text"
              value={newText}
              maxLength={80}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleAddTile(); }
                if (e.key === 'Escape') { setAdding(false); setNewText(''); }
              }}
              onBlur={() => { handleAddTile(); setAdding(false); }}
              placeholder="Name a concept..."
              className="border-2 border-black/30 rounded-lg px-3 h-11 text-base bg-white focus:ring-2 focus:ring-black/30 focus:border-black w-48"
            />
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="bg-white text-navy border-2 border-navy h-11 px-5 rounded-lg flex items-center gap-1.5 font-heading font-bold uppercase tracking-wide text-base hover:bg-navy hover:text-white transition-colors whitespace-nowrap"
            >
              <Plus className="w-5 h-5" /> Add Concept
            </button>
          )}
        </div>
      </div>

      {/* Directional text — bridge between Big Idea and scaffolds */}
      {showScaffoldZones && bigIdeaTile && (
        <div className="flex-shrink-0 bg-white text-center py-1">
          <p className="text-xs text-txt-secondary italic font-body">
            {scaffold1Tile && scaffold2Tile
              ? 'The two things they must know'
              : 'What two things must they know for this to happen?'}
          </p>
        </div>
      )}

      {/* === SLIM PILLARS ROW (visible from Stage 2 onward) === */}
      {showScaffoldZones && (
        <div className="flex-shrink-0 grid grid-cols-2 gap-3 px-3 py-2 bg-white border-b-2 border-black">
          {/* LEFT PILLAR (TEAL) */}
          <div
            ref={(el) => registerZone('SCAFFOLDING_1', el)}
            className={`relative rounded-2xl transition-all flex items-center justify-center text-center px-5 py-2 ${
              hoverZone === 'SCAFFOLDING_1'
                ? 'ring-4 ring-teal-dark scale-[1.01] shadow-2xl'
                : 'shadow-md'
            } ${zoneHighlightClass('SCAFFOLDING_1')}`}
            style={{
              minHeight: 52,
              background: scaffold1Tile
                ? 'linear-gradient(90deg, #1D9E75 0%, #3FBF94 50%, #1D9E75 100%)'
                : '#FFFFFF',
              border: scaffold1Tile ? '3px solid #157A5A' : '3px dashed #157A5A',
            }}
          >
            <div className="absolute -top-2 left-4 px-2 text-[10px] font-heading font-bold uppercase tracking-widest text-teal-dark bg-white">
              Scaffold 1
            </div>
            {scaffold1Tile ? (
              editingZoneTile === scaffold1Tile.id ? (
                <input ref={zoneTileEditRef} type="text" value={editingZoneText} maxLength={80}
                  onChange={(e) => setEditingZoneText(e.target.value)}
                  onBlur={saveZoneTileEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); saveZoneTileEdit(); }
                    if (e.key === 'Escape') setEditingZoneTile(null);
                  }}
                  className="bg-transparent text-black text-center font-heading font-bold text-sm uppercase tracking-wide border-none outline-none focus:ring-0 w-full" />
              ) : (
                <div
                  className="text-black font-heading font-bold text-sm uppercase tracking-wide cursor-grab active:cursor-grabbing text-center line-clamp-2"
                  onPointerDown={(e) => handleTilePointerDown(e, scaffold1Tile)}
                  onDoubleClick={() => { setEditingZoneTile(scaffold1Tile.id); setEditingZoneText(scaffold1Tile.text); }}
                  title={scaffold1Tile.text}
                >
                  {scaffold1Tile.text}
                </div>
              )
            ) : (
              <span className="text-sm text-teal-dark italic font-body font-semibold">
                Drag your first scaffold here
              </span>
            )}
          </div>

          {/* RIGHT PILLAR (GOLD) */}
          <div
            ref={(el) => registerZone('SCAFFOLDING_2', el)}
            className={`relative rounded-2xl transition-all flex items-center justify-center text-center px-5 py-2 ${
              hoverZone === 'SCAFFOLDING_2'
                ? 'ring-4 ring-gold-dark scale-[1.01] shadow-2xl'
                : 'shadow-md'
            } ${zoneHighlightClass('SCAFFOLDING_2')}`}
            style={{
              minHeight: 52,
              background: scaffold2Tile
                ? 'linear-gradient(90deg, #E87722 0%, #F09A50 50%, #E87722 100%)'
                : '#FFFFFF',
              border: scaffold2Tile ? '3px solid #C45E10' : '3px dashed #C45E10',
            }}
          >
            <div className="absolute -top-2 left-4 px-2 text-[10px] font-heading font-bold uppercase tracking-widest text-gold-dark bg-white">
              Scaffold 2
            </div>
            {scaffold2Tile ? (
              editingZoneTile === scaffold2Tile.id ? (
                <input ref={zoneTileEditRef} type="text" value={editingZoneText} maxLength={80}
                  onChange={(e) => setEditingZoneText(e.target.value)}
                  onBlur={saveZoneTileEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); saveZoneTileEdit(); }
                    if (e.key === 'Escape') setEditingZoneTile(null);
                  }}
                  className="bg-transparent text-black text-center font-heading font-bold text-sm uppercase tracking-wide border-none outline-none focus:ring-0 w-full" />
              ) : (
                <div
                  className="text-black font-heading font-bold text-sm uppercase tracking-wide cursor-grab active:cursor-grabbing text-center line-clamp-2"
                  onPointerDown={(e) => handleTilePointerDown(e, scaffold2Tile)}
                  onDoubleClick={() => { setEditingZoneTile(scaffold2Tile.id); setEditingZoneText(scaffold2Tile.text); }}
                  title={scaffold2Tile.text}
                >
                  {scaffold2Tile.text}
                </div>
              )
            ) : (
              <span className="text-sm text-gold-dark italic font-body font-semibold">
                Drag your second scaffold here
              </span>
            )}
          </div>
        </div>
      )}

      {/* Canvas */}
      <div
        ref={viewportRef}
        className={`flex-1 overflow-hidden relative select-none ${
          panning ? 'cursor-grabbing' : dragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onPointerDown={handleViewportPointerDown}
        onWheel={handleWheel}
      >
        {/* === FULL VIEWPORT HALVES — TEAL left, GOLD right, edge to edge === */}
        {showScaffoldZones && (
          <>
            <div className={`absolute top-0 left-0 w-1/2 h-full transition-colors duration-500 pointer-events-none ${
              hoverZone === 'LEFT' ? 'bg-teal/65' : 'bg-teal/40'
            }`} />
            <div className={`absolute top-0 right-0 w-1/2 h-full transition-colors duration-500 pointer-events-none ${
              hoverZone === 'RIGHT' ? 'bg-gold/55' : 'bg-gold/35'
            }`} />
            {/* Single thin midline */}
            <div className="absolute top-0 left-1/2 w-px h-full bg-navy/30 pointer-events-none" />
          </>
        )}

        {/* Transformed canvas — CSS-centered horizontally so browser zoom doesn't shift it */}
        <div
          style={{
            width: CANVAS_W,
            transform: `translateX(${translate.x}px) translateY(${translate.y}px) scale(${scale})`,
            transformOrigin: 'center top',
          }}
          className="mx-auto relative"
        >
          {/* Watermark */}
          <div className="absolute pointer-events-none" style={{ left: MID_X - 96, top: CANVAS_H / 2 - 96 }}>
            <img src="/bcd-icon.png" alt="" className="w-48 opacity-[0.04]" />
          </div>

          {/* === ALL FREELY-POSITIONED TILES (proof + holding pen) === */}
          {tiles
            .filter((t) => t.zone === 'HOLDING_PEN' || t.zone === 'PROOF_1' || t.zone === 'PROOF_2')
            .map((tile) => {
              const isDraggingThis = dragging?.tileId === tile.id;
              const dp = getDisplayPos(tile);
              const x = isDraggingThis ? dragPos?.x : dp.x;
              const y = isDraggingThis ? dragPos?.y : dp.y;
              return (
                <ScatteredTile
                  key={tile.id}
                  tile={tile}
                  x={x}
                  y={y}
                  midX={MID_X}
                  isDragging={isDraggingThis}
                  hidden={isDraggingThis}
                  onPointerDown={(e) => handleTilePointerDown(e, tile)}
                  onUpdateTile={onUpdateTile}
                  onCut={onCutTile}
                />
              );
            })}
        </div>

        {/* Pan detection */}
        <div className="absolute inset-0 pointer-events-none" data-canvas-bg />

        {/* Brain watermark in viewport space */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="/bcd-icon.png" alt="" className="w-64 opacity-[0.02]" />
        </div>

        {/* Zoom controls */}
        <div className="absolute bottom-3 left-3 z-30 flex items-center gap-0 bg-white shadow-sm border border-navy/10 rounded-lg overflow-hidden">
          <button onClick={fitAll}
            className="px-2.5 py-2 text-xs text-txt-secondary hover:text-txt-primary hover:bg-gray-50 transition-all flex items-center gap-1.5 border-r border-navy/10"
            title="Fit all on screen">
            <Maximize className="w-3.5 h-3.5" /> Fit All
          </button>
          <button onClick={() => zoomBy(0.9)}
            className="px-2.5 py-2 text-sm text-txt-secondary hover:text-txt-primary hover:bg-gray-50 transition-colors border-r border-navy/10 font-medium"
            title="Zoom out">&minus;</button>
          <input
            type="text"
            value={zoomPercent + '%'}
            onChange={(e) => {
              const val = parseInt(e.target.value.replace(/\D/g, ''), 10);
              if (!isNaN(val) && val >= MIN_ZOOM * 100 && val <= MAX_ZOOM * 100) {
                setScale(val / 100);
              }
            }}
            onBlur={(e) => {
              const val = parseInt(e.target.value.replace(/\D/g, ''), 10);
              if (!isNaN(val)) {
                setScale(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, val / 100)));
              }
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            className="w-14 py-2 text-xs text-txt-secondary text-center tabular-nums bg-transparent border-none outline-none focus:ring-0 focus:text-txt-primary"
          />
          <button onClick={() => zoomBy(1.1)}
            className="px-2.5 py-2 text-sm text-txt-secondary hover:text-txt-primary hover:bg-gray-50 transition-colors border-l border-navy/10 font-medium"
            title="Zoom in">+</button>
        </div>
      </div>

      {/* Cut bar */}
      <CutBar tiles={cutTiles} onRestore={onRestoreTile} onDelete={onDeleteTile} />

      {/* Drag ghost */}
      {dragging && dragScreenPos && (() => {
        const tile = tiles.find(t => t.id === dragging.tileId);
        if (!tile) return null;
        return (
          <div
            className="fixed pointer-events-none"
            style={{
              left: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().left + dragScreenPos.x : dragScreenPos.x,
              top: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().top + dragScreenPos.y : dragScreenPos.y,
              zIndex: 9999,
            }}
          >
            <div className={`rounded-lg shadow-2xl ring-2 px-3 py-2.5 max-w-[220px] min-w-[120px] ${
              hoverZone ? 'bg-gold/20 ring-gold border border-gold' : 'bg-white ring-gold/40 border border-navy/12'
            }`}>
              <span className="text-sm text-txt-primary font-medium">{tile.text}</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// --- Scattered Tile ---
// midX is passed in so the tile's color reflects its LIVE position, not its stored zone.
// Stale data with mismatched zone/position still renders correctly.
function ScatteredTile({ tile, x, y, isDragging, hidden, onPointerDown, onUpdateTile, onCut, midX }) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(tile.text);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  function saveText() {
    setEditing(false);
    const trimmed = editText.trim();
    if (trimmed && trimmed !== tile.text) onUpdateTile(tile.id, { text: trimmed });
  }

  // Color comes from CURRENT visual position, not stored zone.
  // Holding pen = BLACK pill / white text. Placed tiles get TEAL (left) or GOLD (right) background.
  const isPlaced = tile.zone === 'PROOF_1' || tile.zone === 'PROOF_2';
  const isLeftSide = (x || 0) + 100 < (midX || 600);
  let pillClasses;
  if (!isPlaced) {
    pillClasses = 'bg-navy text-white border-white/15';
  } else if (isLeftSide) {
    pillClasses = 'bg-teal text-black border-teal-dark';
  } else {
    pillClasses = 'bg-gold text-black border-gold-dark';
  }

  return (
    <div
      className={`absolute select-none ${isDragging ? 'z-50' : 'z-10'}`}
      style={{
        left: x, top: y,
        opacity: hidden ? 0.2 : 1,
        transform: isDragging ? 'scale(1.05)' : 'scale(1)',
        transition: isDragging ? 'none' : 'transform 0.3s ease, opacity 0.15s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`${pillClasses} rounded-2xl pill-glow border-[3px]
          px-6 py-3.5 max-w-[280px] min-w-[160px] cursor-grab active:cursor-grabbing
          hover:scale-[1.02] transition-all duration-300`}
        onPointerDown={editing ? undefined : onPointerDown}
        onDoubleClick={() => { setEditText(tile.text); setEditing(true); }}
      >
        {editing ? (
          <input ref={inputRef} type="text" value={editText}
            onChange={(e) => setEditText(e.target.value)} onBlur={saveText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); saveText(); }
              if (e.key === 'Escape') { setEditing(false); setEditText(tile.text); }
            }}
            className="w-full bg-transparent border-none outline-none p-0 text-base font-bold focus:ring-0" />
        ) : (
          <span className="text-base font-bold break-words">{tile.text}</span>
        )}
      </div>

      {hovered && !editing && (
        <button onPointerDown={(e) => e.stopPropagation()} onClick={() => onCut(tile.id)}
          className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white shadow-md border-2 border-black/20 flex items-center justify-center text-txt-secondary hover:text-red-500 hover:border-red-300 transition-colors z-20">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// --- Cut Bar ---
function CutBar({ tiles, onRestore, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex-shrink-0 bg-navy text-white z-20 border-t-2 border-brand-red/60">
      <button onClick={() => setOpen(!open)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-sm hover:bg-navy-light transition-colors relative z-10">
        <span className="flex items-center gap-2">
          <span className="font-heading font-bold uppercase tracking-wider text-xs">Removed Concepts</span>
          {tiles.length > 0 && (
            <span className="bg-brand-red text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">{tiles.length}</span>
          )}
          {tiles.length === 0 && (
            <span className="text-gray-500 text-xs">none</span>
          )}
        </span>
        <span className="text-gray-400 text-xs">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && tiles.length > 0 && (
        <div className="px-4 py-3 flex flex-wrap gap-2 border-t border-white/15">
          {tiles.map((tile) => (
            <div key={tile.id} className="bg-navy-light text-gray-300 rounded-lg px-3 py-2 text-xs flex items-center gap-2 max-w-[220px]">
              <span className="truncate flex-1">{tile.text}</span>
              <button onClick={() => onRestore(tile.id)} className="text-green-400 hover:text-green-300 shrink-0 text-sm" title="Restore">↩</button>
              <button onClick={() => onDelete(tile.id)} className="text-red-400 hover:text-red-300 shrink-0" title="Delete permanently">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
